<template>
  <div class="search-card">
    <!-- 检索类型 -->
    <a-tabs
      v-model:activeKey="activeTab"
      class="search-tabs"
      @change="handleTabChange"
    >
      <a-tab-pane
        v-for="item in tabs"
        :key="item.key"
        :disabled="item.key === 'law'"
      >
        <template #tab>
          <div
            class="tab-title"
            :class="{ 'tab-disabled': item.key === 'law' }"
          >
            <img :src="getTabIcon(item)" class="tab-icon" />
            <span :class="['tab-text', { active: activeTab === item.key }]">{{
              item.title
            }}</span>
          </div>
        </template>

        <!-- tab内容 -->
      </a-tab-pane>
    </a-tabs>
    <div class="search-input">
      <!-- 输入框 -->
      <a-textarea
        v-model:value="form.keyword"
        :rows="6"
        :maxlength="500"
        show-count
        placeholder="请输入案件事实、争议焦点、裁判观点等内容..."
        :bordered="false"
        class="search-textarea"
      />
      <!-- AI优化结果 -->
      <OptimizePanel
        :visible="optimizeVisible"
        :loading="optimizeLoading"
        :original-question="originalQuestion"
        :optimized-question="optimizedQuestion"
        @keep="keepQuestion"
        @close="keepQuestion"
        @replace="replaceQuestion"
        @retry="retryOptimize"
      />
      <!-- 工具栏 -->
      <div class="toolbar">
        <div class="toolbar-left">
          <a-button type="text" @click="uploadFile" class="text-up">
            <img src="@/assets/casesearchImg/icon-up.png" class="icons" />
            加密上传文件
          </a-button>

          <a-button type="text" @click="optimize" class="text-recognition">
            <img
              src="@/assets/casesearchImg/icon-recognition.png"
              class="icons"
            />
            智能优化问题
          </a-button>
        </div>

        <div class="toolbar-right">
          <div class="year-select">
            <img
              src="@/assets/casesearchImg/i-calendar.png"
              class="calendar-icon"
            />

            <a-select
              v-model:value="form.year"
              style="width: 120px"
              placeholder="裁判年份"
              class="select-year"
            >
              <a-select-option
                v-for="item in yearOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </a-select-option>
            </a-select>
          </div>
          <div class="select-wrapper">
            <img
              src="@/assets/casesearchImg/i-building.png"
              class="tree-select-icon"
            />

            <a-tree-select
              v-model:value="form.court"
              style="width: 230px"
              :tree-data="courtTreeData"
              placeholder="法院"
              allow-clear
              show-search
              tree-node-filter-prop="title"
              tree-default-expand-all
            />
          </div>

          <a-button class="button-search" size="large" @click="search">
            <img
              src="@/assets/casesearchImg/i-search.png"
              class="search-icon"
            />

            开始检索
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch, onMounted } from "vue";
import { message } from "ant-design-vue";
import OptimizePanel from "./OptimizePanel.vue";
import { PolishQuestion, GetTree } from "../../api/case/case";
const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "search", "upload", "optimize"]);

const activeTab = ref("case");

const handleTabChange = (key) => {
  if (key === "law") {
    activeTab.value = "case";
    message.info("法规检索功能开发中");
  }
};

// 图标使用 require，Vue CLI 可以直接解析
const tabs = [
  {
    key: "case",
    title: "类案检索",
    icon: "icon1",
  },
  {
    key: "law",
    title: "法规检索",
    icon: "icon2",
  },
];
const getTabIcon = (item) => {
  const suffix = activeTab.value === item.key ? "_2" : "_1";

  return require(`@/assets/casesearchImg/${item.icon}${suffix}.png`);
};
const form = reactive({
  keyword: props.modelValue,
  court: "全部法院",
  year: 0,
});

watch(
  () => form.keyword,
  (val) => emit("update:modelValue", val),
);

const uploadFile = () => {
  emit("upload");
};

const optimize = async () => {
  if (!form.keyword.trim()) {
    return;
  }

  emit("optimize");

  optimizeVisible.value = true;

  optimizeLoading.value = true;

  originalQuestion.value = form.keyword;

  try {
    const res = await PolishQuestion({ question: form.keyword });
    if (res.isSuccess && res.data) {
      originalQuestion.value = res.data.originalQuestion;
      optimizedQuestion.value = res.data.polishedQuestion;
    }
  } catch (err) {
    console.error("智能优化失败", err);
  } finally {
    optimizeLoading.value = false;
  }
};

const search = () => {
  if (form.keyword?.trim()) {
    emit("search", {
      ...form,
      type: activeTab.value,
    });
  } else {
    // 提示逻辑，例如 antd message
    message.warning("请输入搜索关键词");
  }
};
// 保留原问题
const keepQuestion = () => {
  optimizeVisible.value = false;
};

// 使用优化后的问题
const replaceQuestion = (question) => {
  form.keyword = question;

  optimizeVisible.value = false;
};

// 重新优化
const retryOptimize = () => {
  optimize();
};

const yearOptions = [
  { label: "全部", value: 0 },
  { label: "2026年", value: 2026 },
  { label: "2025年", value: 2025 },
  { label: "2024年", value: 2024 },
  { label: "2023年", value: 2023 },
  { label: "2022年", value: 2022 },
  { label: "2021年", value: 2021 },
  { label: "2020年", value: 2020 },
  { label: "2019年", value: 2019 },
  { label: "2018年", value: 2018 },
  { label: "2017年", value: 2017 },
  { label: "2016年", value: 2016 },
];
const courtTreeData = ref([]);

// 加载法院树形数据
const loadCourtTree = async () => {
  try {
    const res = await GetTree();
    if (res.isSuccess && Array.isArray(res.data)) {
      courtTreeData.value = res.data;
    }
  } catch (err) {
    console.error("加载法院数据失败", err);
  }
};

onMounted(() => {
  loadCourtTree();
});
// AI优化面板
const optimizeVisible = ref(false);

const optimizeLoading = ref(false);

// 原始问题
const originalQuestion = ref("");

// AI优化后的问题
const optimizedQuestion = ref("");
</script>

<style scoped lang="less">
.search-card {
  background: #f6f8fc;
  border-radius: 18px;
  // padding: 24px 0;
  // box-shadow: 0 8px 30px rgba(0, 0, 0, .05);
}

.search-tabs {
  margin-bottom: 12px;
}
.tab-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.tab-icon {
  width: 20px;
  height: 20px;
}
.tab-text {
  color: #4d5d78;
  font-weight: 600;
  font-size: 14px;
  transition: color 0.2s;
}

.tab-text.active {
  color: #1768ff;
}
.tab-disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.search-input {
  background-color: #feffff;
  padding: 12px 24px 0px;
}
.search-textarea {
  font-size: 15px;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 18px;
  border: none;
}

.toolbar-left {
  display: flex;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

:deep(.ant-input) {
  resize: none;
  font-size: 15px;
}

:deep(.ant-picker),
:deep(.ant-select-selector) {
  border-radius: 8px !important;
}

:deep(.ant-btn-primary) {
  border-radius: 8px;
  padding: 0 24px;
}
.text-up {
  background: #ffffff;
  box-shadow: 0px 8px 24px 0px rgba(36, 70, 130, 0.08);
  border-radius: 10px 10px 10px 10px;
  border: 1px solid #d8e0ed;
  font-weight: 600;
  font-size: 14px;
  color: #31425e;
  height: 36px;
  text-shadow: 0px 8px 24px rgba(36, 70, 130, 0.08);
}
.icons {
  width: 20px;
  height: 20px;
  margin-right: 8px;
}
.text-recognition {
  background: #f2f7ff;
  box-shadow: 0px 8px 24px 0px rgba(36, 70, 130, 0.08);
  border-radius: 10px 10px 10px 10px;
  border: 1px solid #d9e7ff;
  font-weight: 600;
  font-size: 14px;
  color: #1768ff;
  height: 36px;
}
.text-recognition:hover {
  background: #f2f7ff;
  box-shadow: 0 8px 24px rgba(36, 70, 130, 0.08);
  border: 1px solid #d9e7ff;
  color: #1768ff;
  font-weight: 600;
}
// .select-year{
//   background: #FFFFFF;
//   box-shadow: 0px 8px 24px 0px rgba(36,70,130,0.08);
//   border-radius: 10px 10px 10px 10px;
//   border: 1px solid #D8E0ED;
//   font-weight: 600;
//   font-size: 15px;
//   color: #31425E;
// }
.year-select {
  position: relative;
  display: inline-block;
  background: #ffffff;
  box-shadow: 0px 8px 24px 0px rgba(36, 70, 130, 0.08);
  border-radius: 10px 10px 10px 10px;
  border: 1px solid #d8e0ed;
  font-weight: 600;
  font-size: 15px;
  color: #31425e;
}

.calendar-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  z-index: 10;
  pointer-events: none;
}

.year-select :deep(.ant-select-selector) {
  padding-left: 36px !important;
  /* 外层 .year-select 已有边框，去掉 select 自带的第二层边框 */
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}
/* 聚焦时不再出现内层蓝色描边 */
.year-select :deep(.ant-select-focused .ant-select-selector) {
  box-shadow: none !important;
}

.tree-select-icon {
  width: 16px;
  height: 16px;
  margin-right: 4px;
  vertical-align: middle;
  flex-shrink: 0;
}

.select-wrapper {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  box-shadow: 0px 8px 24px 0px rgba(36, 70, 130, 0.08);
  border-radius: 10px 10px 10px 10px;
  border: 1px solid #d8e0ed;
  font-weight: 600;
  font-size: 15px;
  color: #31425e;
  padding-left: 12px;
}

.select-wrapper :deep(.ant-select-selector) {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

/* 下拉框统一高度：外壳（含上下 1px 边框）视觉 36px，内部 selector 34px */
.year-select :deep(.ant-select-selector),
.select-wrapper :deep(.ant-select-selector) {
  height: 36px !important;
}

/* year-select 的 selector 已去边框，与 select-wrapper 一致按 34px 内容区居中 */
.year-select :deep(.ant-select-single .ant-select-selection-item),
.year-select :deep(.ant-select-single .ant-select-selection-placeholder),
.year-select :deep(.ant-select-selection-search-input) {
  line-height: 34px !important;
}

/* select-wrapper 的 selector 无边框，内容区即 34px */
.select-wrapper :deep(.ant-select-single .ant-select-selection-item),
.select-wrapper :deep(.ant-select-single .ant-select-selection-placeholder) {
  line-height: 34px !important;
}
.year-select :deep(.ant-select-selection-search-input),
.select-wrapper :deep(.ant-select-selection-search-input) {
  height: 32px !important;
}
.button-search {
  background: linear-gradient(162deg, #1568ff 0%, #0f5ce6 100%);
  box-shadow: 0px 8px 24px 0px rgba(36, 70, 130, 0.08),
    0px 8px 18px 0px rgba(23, 104, 255, 0.2);
  border-radius: 10px 10px 10px 10px;
  border: 1px solid #1768ff;
  font-weight: 400;
  font-size: 14px;
  color: #ffffff;
  /* size="large" 默认 40px，与两侧下拉框统一为 36px */
  height: 36px;
}
:where(.css-dev-only-do-not-override-z4nrqd).ant-btn-default:not(
    :disabled
  ):hover {
  color: #ffffff !important;
}
.search-icon {
  width: 20px;
  height: 20px;
  margin-bottom: 4px;
  margin-right: 8px;
}
.search-textarea {
  margin-bottom: 8px;
}

:deep(.optimize-panel) {
  margin: 18px 0;
}

:deep(.search-input .ant-input:focus),
:deep(.search-input .ant-input-focused) {
  border-color: transparent !important;
  box-shadow: none !important;
}
</style>
